class Header {
  constructor() {
    this.header = document.querySelector('[data-header]');
    if (!this.header) return;
    this.scrim = document.querySelector('[data-megamenu-scrim]');
    this.drawer = document.querySelector('[data-header-drawer]');
    this.overlay = document.querySelector('[data-drawer-overlay]');
    this.menuToggle = document.querySelector('[data-menu-toggle]');
    this.drawerClose = this.drawer?.querySelector('[data-drawer-close]');
    this.sticky = this.header.classList.contains('header--sticky');
    this.transparent = this.header.classList.contains('header--transparent');
    this.navItems = this.header.querySelectorAll('[data-nav-item]');
    this.focusTrapActive = false;
    this.lastFocusedElement = null;
    this.countdownInterval = null;
    this.scrollRafId = null;
    this.swipeHandlers = null;
    this.announcementInterval = null;
    this.init();
  }

  init() {
    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', () => this.toggleDrawer());
    }
    if (this.drawerClose) {
      this.drawerClose.addEventListener('click', () => this.closeDrawer());
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeDrawer());
    }
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDrawer();
        this.closeAllDisclosures();
      }
      if (this.focusTrapActive && e.key === 'Tab') {
        this.trapFocus(e);
      }
    });
    this.initDisclosures();
    this.initDropdowns();
    this.initDrawerToggles();
    this.initCountdown();
    this.initAnnouncementRotation();
    this.initAnnouncementInfo();
    this.initKeyboardNav();
    this.initCartCountBounce();
    this.initUtilityAnnouncement();
    this.initDrawerSearch();
    if (this.sticky) {
      this.initSticky();
    }
  }

  getFocusableElements() {
    if (!this.drawer) return [];
    return Array.from(this.drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
  }

  trapFocus(e) {
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  enableFocusTrap() {
    this.focusTrapActive = true;
  }

  disableFocusTrap() {
    this.focusTrapActive = false;
  }

  initKeyboardNav() {
    this.navItems.forEach((item) => {
      const link = item.querySelector('.header__nav-link');
      if (!link) return;

      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const dropdown = item.querySelector('[data-nav-dropdown]');
          if (item.classList.contains('is-open')) {
            this.closeAllMega();
          } else if (dropdown) {
            this.openMega(item, link, dropdown);
          }
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const dropdown = item.querySelector('[data-nav-dropdown]');
          if (dropdown) {
            this.openMega(item, link, dropdown);
            const firstLink = dropdown.querySelector('a');
            if (firstLink) firstLink.focus();
          }
        }
        if (e.key === 'Escape') {
          this.closeAllMega();
        }
      });
    });

    document.querySelectorAll('[data-nav-dropdown]').forEach((dropdown) => {
      const links = Array.from(dropdown.querySelectorAll('a'));
      links.forEach((link, index) => {
        link.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (links[index + 1]) links[index + 1].focus();
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (links[index - 1]) links[index - 1].focus();
          }
          if (e.key === 'Escape') {
            const parentItem = link.closest('[data-nav-item]');
            if (parentItem) {
              this.closeAllMega();
              const parentLink = parentItem.querySelector('.header__nav-link');
              if (parentLink) parentLink.focus();
            }
          }
        });
      });
    });
  }

  closeAllDisclosures() {
    this.header.querySelectorAll('[data-disclosure].is-open').forEach(d => {
      d.classList.remove('is-open');
      const toggle = d.querySelector('[data-disclosure-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }

  initDisclosures() {
    this.header.querySelectorAll('[data-disclosure]').forEach(disclosure => {
      const toggle = disclosure.querySelector('[data-disclosure-toggle]');
      if (!toggle) return;

      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const wasOpen = disclosure.classList.contains('is-open');
        this.closeAllDisclosures();
        if (!wasOpen) {
          disclosure.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-disclosure]')) {
        this.closeAllDisclosures();
      }
    });
  }

  initCountdown() {
    const countdownElements = document.querySelectorAll('[data-countdown]');
    if (countdownElements.length === 0) return;

    const updateCountdown = () => {
      countdownElements.forEach(el => {
        const targetDate = new Date(el.dataset.countdown).getTime();
        const now = new Date().getTime();
        const distance = targetDate - now;

        if (distance < 0) {
          el.textContent = el.dataset.expiredText || 'EXPIRED';
          return;
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        el.textContent = `${days}d ${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
      });
    };

    updateCountdown();
    this.countdownInterval = setInterval(updateCountdown, 1000);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
      } else {
        updateCountdown();
        this.countdownInterval = setInterval(updateCountdown, 1000);
      }
    });
  }

  /* Info (ⓘ) toggle next to the announcement message: opens a small
     disclaimer box. Closes on outside click or Escape */
  initAnnouncementInfo() {
    const toggle = document.querySelector('[data-announcement-info-toggle]');
    const box = document.querySelector('[data-announcement-info-box]');
    if (!toggle || !box) return;

    const close = () => {
      box.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    };

    toggle.addEventListener('click', (event) => {
      event.stopPropagation();
      const willOpen = box.hidden;
      box.hidden = !willOpen;
      toggle.setAttribute('aria-expanded', String(willOpen));
    });

    document.addEventListener('click', (event) => {
      if (!box.hidden && !box.contains(event.target)) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  }

  initAnnouncementRotation() {
    const bar = document.querySelector('[data-announcement-bar]');
    if (!bar) return;

    const slides = bar.querySelectorAll('[data-announcement-slide]');
    if (slides.length <= 1) return;

    let currentIndex = 0;
    const interval = 4000;

    this.announcementInterval = setInterval(() => {
      slides[currentIndex].classList.remove('is-active');
      currentIndex = (currentIndex + 1) % slides.length;
      slides[currentIndex].classList.add('is-active');
    }, interval);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (this.announcementInterval) {
          clearInterval(this.announcementInterval);
          this.announcementInterval = null;
        }
      } else {
        this.announcementInterval = setInterval(() => {
          slides[currentIndex].classList.remove('is-active');
          currentIndex = (currentIndex + 1) % slides.length;
          slides[currentIndex].classList.add('is-active');
        }, interval);
      }
    });
  }

  initDropdowns() {
    // Position dropdowns below the nav row (fixed top), recalculated on
    // scroll so it follows the collapsing header (when Row 1 hides on desktop)
    const positionDropdowns = () => {
      const navRow = this.header.querySelector('.header__row--nav');
      if (!navRow) return;
      const navBottom = navRow.getBoundingClientRect().bottom;
      this.navItems.forEach((item) => {
        const dropdown = item.querySelector('[data-nav-dropdown]');
        if (dropdown) dropdown.style.top = `${navBottom}px`;
      });
      // Scrim starts below the header so it never darkens it
      if (this.scrim) {
        this.scrim.style.top = `${this.header.getBoundingClientRect().bottom}px`;
      }
    };

    const onPositionChange = () => positionDropdowns();
    positionDropdowns();
    window.addEventListener('resize', onPositionChange);
    /* Only reposition dropdowns on scroll if they exist (desktop mega menu).
       On mobile there are no dropdowns — avoid getBoundingClientRect
       layout reads on every scroll frame (jitter source). */
    if (this.navItems.length > 0) {
      window.addEventListener('scroll', onPositionChange, { passive: true });
    }

    this.navItems.forEach((item) => {
      const link = item.querySelector('.header__nav-link');
      const dropdown = item.querySelector('[data-nav-dropdown]');
      if (!dropdown) return;
      item.addEventListener('mouseenter', () => {
        this.openMega(item, link, dropdown);
      });
    });

    // Close when the pointer leaves any open item and its dropdown
    document.addEventListener('mouseover', (e) => {
      const overOpen = this.header.contains(e.target) &&
        e.target.closest('.header__nav-item.is-open') !== null;
      const anyOpen = this.header.querySelector('.header__nav-item.is-open');
      if (anyOpen && !overOpen) {
        this.closeAllMega();
      }
    });
  }

  openMega(item, link, dropdown) {
    const currently = this.header.querySelector('.header__nav-item.is-open');
    if (currently && currently !== item) {
      currently.classList.remove('is-open');
      const curLink = currently.querySelector('.header__nav-link');
      const curDropdown = currently.querySelector('[data-nav-dropdown]');
      curDropdown?.setAttribute('aria-hidden', 'true');
      curLink?.setAttribute('aria-expanded', 'false');
    }
    item.classList.add('is-open');
    this.header.classList.add('header--has-mega-open');
    this.scrim?.classList.add('is-visible');
    this.scrim?.removeAttribute('hidden');
    dropdown.setAttribute('aria-hidden', 'false');
    link?.setAttribute('aria-expanded', 'true');
  }

  closeAllMega() {
    this.navItems.forEach((it) => {
      it.classList.remove('is-open');
      const d = it.querySelector('[data-nav-dropdown]');
      const l = it.querySelector('.header__nav-link');
      d?.setAttribute('aria-hidden', 'true');
      l?.setAttribute('aria-expanded', 'false');
    });
    this.header.classList.remove('header--has-mega-open');
    this.scrim?.classList.remove('is-visible');
  }

  initDrawerToggles() {
    this.drawer?.querySelectorAll('[data-drawer-next]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.getAttribute('data-panel-target');
        const panel = this.drawer.querySelector(`[data-drawer-panel][data-panel="${CSS.escape(target)}"]`);
        if (!panel) return;
        this.drawer.querySelectorAll('[data-drawer-panel].is-open').forEach((p) => {
          p.classList.remove('is-open');
          p.querySelector('[data-drawer-back]')?.setAttribute('aria-expanded', 'false');
        });
        panel.classList.add('is-open');
        this.drawer.classList.add('has-panel-open');
        btn.setAttribute('aria-expanded', 'true');
      });
    });

    this.drawer?.querySelectorAll('[data-drawer-back]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = btn.closest('[data-drawer-panel]');
        if (!panel) return;
        panel.classList.remove('is-open');
        this.drawer.classList.remove('has-panel-open');
        panel.querySelector('[data-drawer-sublist].is-open')?.classList.remove('is-open');
        panel.querySelectorAll('[data-drawer-item].is-open').forEach((item) => {
          item.classList.remove('is-open');
          item.querySelector('[data-drawer-toggle]')?.setAttribute('aria-expanded', 'false');
        });
        btn.setAttribute('aria-expanded', 'false');
      });
    });

    this.drawer?.querySelectorAll('[data-drawer-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const row = btn.closest('.header-drawer__item-row');
        const region = row?.nextElementSibling?.matches('[data-drawer-sublist]')
          ? row.nextElementSibling
          : null;
        if (!region) return;
        const isOpen = region.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(isOpen));
      });
    });
  }

  initDrawerSearch() {
    const drawerSearch = document.querySelector('[data-drawer-search]');
    if (!drawerSearch) return;
    drawerSearch.addEventListener('click', () => {
      this.closeDrawer();
      setTimeout(() => {
        document.querySelector('[data-search-toggle]')?.click();
      }, 300);
    });
  }

  /* ── Utility bar: free-shipping message ──
     The threshold is ONE number in the store's currency (data-threshold-cents,
     shop.currency). The cart can be in another Markets currency; then the
     threshold is converted with the storefront rate Shopify publishes
     (Shopify.currency.rate = units of the active currency per store unit).
     Amounts are formatted from a Liquid `money` sample of the ACTIVE currency,
     so JS output is byte-identical to the server render (no "$ 30", no
     "30,00 $", no double symbol). */
  initUtilityAnnouncement() {
    const utilityRow = this.header.querySelector('[data-utility-row]');
    if (!utilityRow) return;
    this.initUtilityMarquee(utilityRow);

    const wrapper = utilityRow.querySelector('[data-utility-announcement-wrap]');
    const announcementEl = utilityRow.querySelector('[data-utility-announcement]');
    const cloneEl = utilityRow.querySelector('[data-utility-clone]');
    if (!announcementEl) return;

    const data = announcementEl.dataset;
    const shopCurrency = data.shopCurrency || '';
    const cartCurrency = data.fsCurrency || shopCurrency;
    const baseThresholdCents = parseInt(data.thresholdCents, 10) || 0;
    const templates = {
      remaining: data.remainingText || '',
      empty: data.emptyText || '',
      qualified: data.qualifiedText || ''
    };
    const formatMoney = Header.moneyFormatterFromSample(data.moneySample, cartCurrency);

    if (wrapper) {
      wrapper.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('cart:open'));
      });
    }

    // Threshold in the cart's currency (cents).
    const thresholdFor = (currency) => {
      if (!currency || currency === shopCurrency) return baseThresholdCents;
      const rate = Header.presentmentRate(currency);
      if (!rate) return null;
      // Round UP to a whole unit of the buyer's currency: a clean amount, and
      // never lower than what checkout will actually require.
      return Math.ceil((baseThresholdCents / 100) * rate) * 100;
    };

    let lastHtml = null;
    const render = (html) => {
      wrapper?.classList.remove('is-pending');
      if (html === lastHtml) return;
      lastHtml = html;
      announcementEl.innerHTML = html;
      if (cloneEl) cloneEl.innerHTML = html;
      this.refreshUtilityMarquee?.();
    };

    const strong = (cents) => `<strong class="header__utility-amount">${formatMoney(cents)}</strong>`;

    const update = (cart) => {
      if (!cart) return;
      const currency = cart.currency || cartCurrency;
      if (currency !== cartCurrency) {
        // Market changed without a reload: the money sample no longer
        // matches. The next page load renders it right; don't guess.
        return;
      }
      const thresholdCents = thresholdFor(currency);
      if (thresholdCents === null) return; // no rate → stays hidden, never wrong
      const totalCents = Number(cart.total_price) || 0;
      const remainingCents = Math.max(thresholdCents - totalCents, 0);
      const itemCount = Number(cart.item_count) || 0;
      let html;
      if (remainingCents === 0) {
        html = templates.qualified;
      } else if (itemCount === 0) {
        html = templates.empty.split('[threshold]').join(strong(thresholdCents));
      } else {
        html = templates.remaining
          .split('[threshold]').join(strong(thresholdCents))
          .split('[remaining]').join(strong(remainingCents));
      }
      render(html);
    };

    const refetch = () => fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then(update)
      .catch(() => {});

    // Some dispatchers send the /cart/add.js line item, not the cart: only
    // trust payloads that carry the cart total, otherwise ask for the cart.
    document.addEventListener('cart:updated', (e) => {
      const detail = e.detail;
      if (detail && typeof detail.total_price === 'number' && typeof detail.item_count === 'number') {
        update(detail);
      } else {
        refetch();
      }
    });

    // First paint: the server already rendered the exact state when the cart
    // is in the store currency. Otherwise convert right away from the
    // server-side cart snapshot (no network wait), then confirm with /cart.js.
    if (wrapper?.classList.contains('is-pending')) {
      update({
        currency: cartCurrency,
        total_price: parseInt(data.fsCartTotal, 10) || 0,
        item_count: parseInt(data.fsItemCount, 10) || 0
      });
    }
    refetch();
    // bfcache restores can show a stale cart
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) refetch();
    });
  }

  /* Mobile marquee: only scroll when the message does not fit its box. */
  initUtilityMarquee(utilityRow) {
    if (!utilityRow.classList.contains('header__utility--marquee')) return;
    const marquee = utilityRow.querySelector('[data-utility-marquee]');
    const item = marquee?.querySelector('.header__utility-marquee-item:not([data-utility-clone])');
    if (!marquee || !item) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const measure = () => {
      // Compare without the scrolling padding so the check is stable
      // (remove + re-add happen in the same task → no visible flicker).
      marquee.classList.remove('is-scrolling');
      const overflowing = item.scrollWidth > marquee.clientWidth + 1;
      marquee.classList.toggle('is-scrolling', overflowing && !reduceMotion.matches);
    };
    let raf = null;
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = null;
        measure();
      });
    };
    this.refreshUtilityMarquee = measure;
    measure();
    window.addEventListener('resize', onResize, { passive: true });
    reduceMotion.addEventListener?.('change', measure);
    document.fonts?.ready?.then(measure);
  }

  /* Units of `currency` per unit of the store currency, as published by
     Shopify on the storefront (content_for_header: Shopify.currency =
     {"active":"COP","rate":"3306.6"}). */
  static presentmentRate(currency) {
    const fromObject = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      if (obj.active && obj.active !== currency) return null;
      const rate = parseFloat(obj.rate);
      return rate > 0 ? rate : null;
    };
    const shopify = window.Shopify || {};
    const native = fromObject(shopify.currency);
    if (native) return native;
    if (shopify.currencyRate && (!shopify.currencyActive || shopify.currencyActive === currency)) {
      const rate = parseFloat(shopify.currencyRate);
      if (rate > 0) return rate;
    }
    // STOPGAP: theme.liquid overwrites window.Shopify.currency with the bare
    // ISO code, so read the rate back from Shopify's own inline script.
    if (Header._scrapedCurrency === undefined) {
      Header._scrapedCurrency = null;
      const pattern = /Shopify\.currency\s*=\s*(\{[^;{}]*\})\s*;/;
      for (const script of document.querySelectorAll('script:not([src])')) {
        const text = script.textContent;
        if (!text || text.indexOf('Shopify.currency') === -1) continue;
        const match = text.match(pattern);
        if (!match) continue;
        try {
          Header._scrapedCurrency = JSON.parse(match[1]);
        } catch (e) { /* keep looking */ }
        if (Header._scrapedCurrency) break;
      }
    }
    return fromObject(Header._scrapedCurrency);
  }

  /* Builds cents → string from the Liquid `money` output for 1,234,567.00 in
     the active currency ("$1,234,567.00", "$1.234.567,00", "1 234 567,00 €",
     "$1,234,567"...). Falls back to the theme's window.formatMoney. */
  static moneyFormatterFromSample(sample, currency) {
    const fallback = (cents) => (typeof window.formatMoney === 'function'
      ? window.formatMoney(cents, currency)
      : (cents / 100).toFixed(2));
    if (!sample) return fallback;
    const match = sample.match(/1(?<sep>[^\d]{0,6}?)234\k<sep>567(?:([.,])00)?/);
    if (!match) return fallback;
    const prefix = sample.slice(0, match.index);
    const suffix = sample.slice(match.index + match[0].length);
    const thousands = match[1];
    const decimal = match[2] || '';
    return (cents) => {
      const value = Math.max(Number(cents) || 0, 0) / 100;
      const fixed = value.toFixed(decimal ? 2 : 0);
      const [int, frac] = fixed.split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return `${prefix}${grouped}${decimal ? decimal + frac : ''}${suffix}`;
    };
  }

  toggleDrawer() {
    const isOpen = this.drawer?.classList.toggle('is-open');
    this.overlay?.classList.toggle('is-visible');
    document.body.classList.toggle('drawer-open');

    if (isOpen) {
      this.lastFocusedElement = document.activeElement;
      this.menuToggle?.setAttribute('aria-expanded', 'true');
      this.menuToggle?.classList.add('is-open');
      this.enableFocusTrap();
      requestAnimationFrame(() => {
        const firstFocusable = this.drawer?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        firstFocusable?.focus();
      });
    } else {
      this.menuToggle?.setAttribute('aria-expanded', 'false');
      this.disableFocusTrap();
      this.lastFocusedElement?.focus();
      this.resetDrawerPanels();
    }
  }

  resetDrawerPanels() {
    this.drawer?.classList.remove('has-panel-open');
    this.drawer?.querySelectorAll('[data-drawer-panel].is-open').forEach((panel) => {
      panel.classList.remove('is-open');
    });
    this.drawer?.querySelectorAll('[data-drawer-sublist].is-open').forEach((region) => {
      region.classList.remove('is-open');
    });
    this.drawer?.querySelectorAll('[data-drawer-item].is-open').forEach((item) => {
      item.classList.remove('is-open');
      item.querySelector('[data-drawer-toggle]')?.setAttribute('aria-expanded', 'false');
    });
    this.drawer?.querySelectorAll('[data-drawer-next]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  closeDrawer() {
    if (!this.drawer?.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    this.overlay?.classList.remove('is-visible');
    document.body.classList.remove('drawer-open');
    this.menuToggle?.setAttribute('aria-expanded', 'false');
    this.menuToggle?.classList.remove('is-open');
    this.disableFocusTrap();
    this.lastFocusedElement?.focus();
  }

  /* ── Cart Count Bounce ── */
  initCartCountBounce() {
    const cartCounts = document.querySelectorAll('[data-cart-count]');
    if (cartCounts.length === 0) return;

    const syncHasItems = () => {
      document.querySelectorAll('.header__cart-btn').forEach((btn) => {
        const countEl = btn.querySelector('[data-cart-count]');
        const has = countEl && !countEl.hasAttribute('hidden') && parseInt(countEl.textContent, 10) > 0;
        btn.classList.toggle('has-items', !!has);
      });
    };

    const observer = new MutationObserver((mutations) => {
      let changed = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData' || mutation.type === 'childList') {
          const el = mutation.target.closest?.('[data-cart-count]') || mutation.target;
          if (el?.classList?.contains('header__cart-count')) {
            el.classList.remove('is-bouncing');
            void el.offsetWidth;
            el.classList.add('is-bouncing');
            changed = true;
          }
        }
      });
      if (changed) syncHasItems();
    });

    cartCounts.forEach((el) => {
      observer.observe(el, { childList: true, characterData: true, subtree: true, attributes: true });
    });

    syncHasItems();
  }

  /* ── Sticky Header ── */
  /* The announcement wrap is relocated OUT of the sticky section wrapper into
     normal body flow, so it simply scrolls away with the page — no animation,
     no per-frame work. The wrapper keeps native position:sticky pinning.
     Skipped in the theme editor so section mapping stays intact. */
  initSticky() {
    const rowTop = this.header.querySelector('.header__row--top');
    const announcementWrap = this.header.querySelector('.announcement-bar-wrap');
    let lastScroll = 0;
    const stickyBehavior = this.header.dataset.stickyBehavior || 'show-on-scroll';

    // Move search + cart from Row 1 into the nav row when collapsed (desktop)
    const desktopMq = window.matchMedia('(min-width: 950px)');
    const actionsSource = this.header.querySelector('.header__right');
    const navActions = this.header.querySelector('[data-nav-actions]');
    const moveActions = (toNav) => {
      if (!actionsSource || !navActions) return;
      if (!desktopMq.matches) return;
      const icons = this.header.querySelectorAll('[data-search-toggle], .header__cart-btn');
      icons.forEach((icon) => {
        const from = toNav ? actionsSource : navActions;
        const to = toNav ? navActions : actionsSource;
        if (icon.parentElement === from) {
          to.appendChild(icon);
        }
      });
    };

    if (announcementWrap && !window.Shopify?.designMode) {
      const sectionWrapper = this.header.closest('.shopify-section');
      if (sectionWrapper && sectionWrapper.nextElementSibling) {
        sectionWrapper.parentNode.insertBefore(announcementWrap, sectionWrapper.nextSibling);
      }
    }

    const getBaseOffset = () => {
      const utilH = this.header.querySelector('[data-utility-row]')?.offsetHeight || 0;
      const topH = rowTop?.offsetHeight || 0;
      return utilH + topH + 10;
    };

    const PAST_BUFFER = 80;

    window.addEventListener('scroll', () => {
      if (this.scrollRafId) return;
      this.scrollRafId = requestAnimationFrame(() => {
        this.scrollRafId = null;
        const scrollY = window.scrollY;
        const base = getBaseOffset();
        const pastOffset = scrollY > base;

        this.header.classList.toggle('is-pinned', scrollY > 0);
        /* Row 1 collapses only on desktop; CSS gates it behind min-width:950px */
        this.header.classList.toggle('is-scrolled-past', pastOffset);
        moveActions(pastOffset);

        if (this.transparent) {
          this.header.classList.toggle('header--transparent', !pastOffset && !this.header.classList.contains('is-scrolled-past'));
        }

        if (stickyBehavior === 'hide-on-scroll') {
          this.header.classList.toggle('is-hidden', pastOffset && scrollY > lastScroll);
        } else {
          this.header.classList.remove('is-hidden');
        }

        lastScroll = scrollY;
      });
    }, { passive: true });

    // Initial state + breakpoint changes
    moveActions(this.header.classList.contains('is-scrolled-past'));
    const onResize = () => {
      const past = this.header.classList.contains('is-scrolled-past');
      moveActions(past);
    };
    desktopMq.addEventListener?.('change', onResize);
    window.addEventListener('resize', onResize);
  }
}
document.addEventListener('DOMContentLoaded', () => new Header());

/*
 * Session state after signing in (new customer accounts).
 * Sign-in happens on Shopify's account pages; coming back with the browser's
 * Back button restores this page from the back/forward cache — a frozen copy
 * rendered BEFORE the login, so the header still says "Iniciar sesión" although
 * the session exists. When the shopper left through an account link, a restored
 * page is reloaded so Liquid renders the real `customer` state.
 */
(function () {
  var KEY = 'aldo:account-nav';
  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-account-link]')) return;
    try { window.sessionStorage.setItem(KEY, '1'); } catch (e) { /* private mode */ }
  });
  window.addEventListener('pageshow', function (event) {
    var pending = false;
    try {
      pending = window.sessionStorage.getItem(KEY) === '1';
      window.sessionStorage.removeItem(KEY);
    } catch (e) { /* private mode */ }
    if (event.persisted && pending) window.location.reload();
  });
})();
